/**
 * Apply a ReactFlow v11 `useReactFlow().setNodes()` batch without reverting
 * newer React state.
 *
 * In controlled mode, ReactFlow's instance `setNodes(payload)` runs `payload`
 * against its INTERNAL store snapshot (`store.getNodes()`) and emits one
 * `{ type: 'reset', item }` change per resulting node. `applyNodeChanges`
 * then replaces the whole React node array with those items. The store only
 * catches up with React state in a passive effect, so any React update queued
 * since the last sync (e.g. useYCanvas merging a YDoc `label` write) is
 * silently overwritten by the snapshot copy — the stale-label bug (W2,
 * RFC-1548 follow-up). The data-only fast path then preserves identity for
 * that node, so the stale value sticks until the next full rebuild.
 *
 * This reconciles the batch against the LATEST React state instead:
 *   - a node the caller did not touch (`item === snapshot node`) keeps its
 *     current React object;
 *   - a node the caller changed gets only the caller's top-level and `data`
 *     deltas (relative to the snapshot) applied onto its current React object;
 *   - a node the caller added (absent from the snapshot) is added;
 *   - a node present in the snapshot but omitted from the batch is removed;
 *   - a node removed from React state after the snapshot is NOT resurrected;
 *   - a node added to React state after the snapshot is kept.
 * Order follows the caller's batch (callers rely on parent-before-child
 * ordering from `deriveGroupedNodeLayout`); post-snapshot additions follow.
 *
 * Invariant: `items` must derive from `snapshot` (the functional-payload form
 * `setNodes((nds) => ...)`, which every console caller uses). Deltas are
 * computed against the snapshot, so a top-level key absent from an item is
 * treated as a deliberate removal (ReactFlow-measured keys excepted). Removing
 * a `data` key is not expressible this way: callers clear a data field by
 * setting it to `undefined`, which is applied like any other change.
 */
import type { Node } from 'reactflow'

export interface ResetReconcileStats {
  untouched: number
  patched: number
  added: number
  removed: number
  keptNewer: number
  droppedDeleted: number
}

export interface ResetReconcileResult<T extends Node> {
  nodes: T[]
  stats: ResetReconcileStats
}

/** Top-level ReactFlow node fields (everything except `data` and internals). */
type NodeFieldKey = Exclude<keyof Node, symbol | 'data'>

// Exhaustive by construction: `satisfies` fails to compile if ReactFlow adds or
// removes a node field, so the delta below can never silently skip one.
const NODE_FIELD_KEYS = {
  id: true,
  position: true,
  type: true,
  style: true,
  className: true,
  sourcePosition: true,
  targetPosition: true,
  hidden: true,
  selected: true,
  dragging: true,
  draggable: true,
  selectable: true,
  connectable: true,
  deletable: true,
  dragHandle: true,
  width: true,
  height: true,
  parentNode: true,
  parentId: true,
  zIndex: true,
  extent: true,
  expandParent: true,
  positionAbsolute: true,
  ariaLabel: true,
  focusable: true,
  resizing: true,
} satisfies Record<NodeFieldKey, true>

function isNodeFieldKey(key: string): key is NodeFieldKey {
  return key in NODE_FIELD_KEYS
}

const NODE_FIELDS: NodeFieldKey[] = Object.keys(NODE_FIELD_KEYS).filter(isNodeFieldKey)

// ReactFlow-measured/interaction keys: only ever adopt them when the caller
// set them, never clear them because a payload object lacked them.
const MEASURED_KEYS = new Set<NodeFieldKey>(['width', 'height', 'positionAbsolute', 'dragging', 'resizing'])

function copyField<T extends Node, K extends NodeFieldKey>(target: T, source: T, key: K): void {
  target[key] = source[key]
}

/** `data` entries the caller added or changed relative to the snapshot. */
function changedDataEntries(before: object, after: object) {
  const beforeValues = new Map(Object.entries(before))
  return Object.entries(after).filter(
    ([key, value]) => !beforeValues.has(key) || !Object.is(beforeValues.get(key), value),
  )
}

function applyCallerDelta<T extends Node>(current: T, snapshot: T, item: T): T {
  let changed = false
  const next: T = { ...current }
  for (const key of NODE_FIELDS) {
    if (Object.is(item[key], snapshot[key])) continue
    // A key absent from the item reads as `undefined`, which clears it on the
    // live node (a deliberate removal) — except ReactFlow-measured keys.
    if (!(key in item) && MEASURED_KEYS.has(key)) continue
    copyField(next, item, key)
    changed = true
  }
  if (item.data !== snapshot.data) {
    const changes = changedDataEntries(snapshot.data ?? {}, item.data ?? {})
    if (changes.length > 0) {
      next.data = { ...current.data, ...Object.fromEntries(changes) }
      changed = true
    }
  }
  return changed ? next : current
}

export function reconcileReactFlowReset<T extends Node>(
  current: readonly T[],
  snapshot: readonly T[],
  items: readonly T[],
): ResetReconcileResult<T> {
  const stats: ResetReconcileStats = {
    untouched: 0,
    patched: 0,
    added: 0,
    removed: 0,
    keptNewer: 0,
    droppedDeleted: 0,
  }
  const currentById = new Map(current.map((n) => [n.id, n]))
  const snapshotById = new Map(snapshot.map((n) => [n.id, n]))
  const itemIds = new Set(items.map((n) => n.id))
  const result: T[] = []

  for (const item of items) {
    const live = currentById.get(item.id)
    const snap = snapshotById.get(item.id)
    if (!snap) {
      // Caller-added node. If React state already has it, keep the live copy.
      result.push(live ?? item)
      stats.added += live ? 0 : 1
      continue
    }
    if (!live) {
      // Removed from React state after the snapshot (e.g. YDoc delete).
      stats.droppedDeleted++
      continue
    }
    if (item === snap) {
      result.push(live)
      stats.untouched++
      continue
    }
    const patched = applyCallerDelta(live, snap, item)
    result.push(patched)
    if (patched !== live) stats.patched++
    else stats.untouched++
  }

  for (const snap of snapshot) {
    if (!itemIds.has(snap.id) && currentById.has(snap.id)) stats.removed++
  }

  for (const live of current) {
    if (!snapshotById.has(live.id) && !itemIds.has(live.id)) {
      result.push(live)
      stats.keptNewer++
    }
  }

  return { nodes: result, stats }
}
